import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import {
  fetchRecentReviews,
  approveReview,
  rejectReview,
  deleteAdminReview,
} from "./adminApi";

export default function ReviewModeration() {
  const navigate = useNavigate();

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  const getReviewImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (/^https?:\/\//i.test(imagePath)) return imagePath;
    return `${backendUrl}${imagePath}`;
  };

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0,
  });
  const [selectedReview, setSelectedReview] = useState(null);
  const [rejectReason, setRejectReason] = useState("Inappropriate content");
  const [actionLoading, setActionLoading] = useState(false);

  const loadReviews = async (limit = 20, offset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRecentReviews({ limit, offset });
      setReviews(data.reviews || []);
      setPagination({
        limit,
        offset,
        total: data.total || 0,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const handleApprove = async (reviewId) => {
    try {
      setActionLoading(true);
      await approveReview(reviewId);
      setReviews((prev) =>
        prev.map((r) =>
          r.review_id === reviewId
            ? {
                ...r,
                approved: true,
                flagged_for_review: false,
                rejection_reason: null,
              }
            : r,
        ),
      );
      setSelectedReview((prev) =>
        prev?.review_id === reviewId
          ? {
              ...prev,
              approved: true,
              flagged_for_review: false,
              rejection_reason: null,
            }
          : prev,
      );
    } catch (err) {
      setError("Failed to approve review");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (reviewId) => {
    try {
      setActionLoading(true);
      await rejectReview(reviewId, rejectReason);
      setReviews((prev) =>
        prev.map((r) =>
          r.review_id === reviewId
            ? {
                ...r,
                approved: false,
                flagged_for_review: false,
                rejection_reason: rejectReason,
              }
            : r,
        ),
      );
      setSelectedReview((prev) =>
        prev?.review_id === reviewId
          ? {
              ...prev,
              approved: false,
              flagged_for_review: false,
              rejection_reason: rejectReason,
            }
          : prev,
      );
    } catch (err) {
      setError("Failed to reject review");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm("This will permanently delete the review. Continue?")) {
      return;
    }
    try {
      setActionLoading(true);
      await deleteAdminReview(reviewId);
      setReviews((prev) => prev.filter((r) => r.review_id !== reviewId));
      setSelectedReview((prev) => (prev?.review_id === reviewId ? null : prev));
    } catch (err) {
      setError("Failed to delete review");
    } finally {
      setActionLoading(false);
    }
  };

  const handleNextPage = () => {
    const newOffset = pagination.offset + pagination.limit;
    if (newOffset < pagination.total) {
      loadReviews(pagination.limit, newOffset);
    }
  };

  const handlePreviousPage = () => {
    if (pagination.offset > 0) {
      const newOffset = Math.max(0, pagination.offset - pagination.limit);
      loadReviews(pagination.limit, newOffset);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate("/admin")}
          className="text-gray-600 hover:text-gray-900 transition-colors"
          title="Back to Dashboard"
        >
          <FaArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Review Moderation
        </h1>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Reviews List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  All Reviews ({pagination.total})
                </h2>
              </div>

              {loading && reviews.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="inline-block animate-spin">
                    <svg
                      className="h-8 w-8 text-blue-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                  <p className="mt-4 text-gray-500">Loading reviews...</p>
                </div>
              ) : reviews.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <p className="text-lg">No reviews yet</p>
                </div>
              ) : (
                <div className="overflow-y-auto max-h-[600px]">
                  {reviews.map((review) =>
                    (() => {
                      const reviewImageUrl = getReviewImageUrl(
                        review.image_url,
                      );
                      return (
                        <div
                          key={review.review_id}
                          onClick={() => setSelectedReview(review)}
                          className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition ${
                            selectedReview?.review_id === review.review_id
                              ? "bg-blue-50"
                              : ""
                          }`}
                        >
                          <div className="flex justify-between items-start gap-3">
                            {reviewImageUrl && (
                              <img
                                src={reviewImageUrl}
                                alt="Review"
                                className="w-16 h-16 rounded object-cover border border-gray-200 shrink-0"
                              />
                            )}
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">
                                {review.User?.first_name || ""}{" "}
                                {review.User?.last_name || "Customer"}
                              </p>
                              <p className="text-xs text-gray-500 mb-1">
                                {review.Product?.name_en || "Unknown product"}
                              </p>
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {review.comment || "No comment"}
                              </p>
                              <div className="flex gap-2 mt-2 flex-wrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {review.rating} ⭐
                                </span>
                                {review.approved ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Approved
                                  </span>
                                ) : review.rejection_reason ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Rejected
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    Pending
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })(),
                  )}
                </div>
              )}

              {/* Pagination */}
              {pagination.total > pagination.limit && (
                <div className="p-4 border-t border-gray-200 flex justify-between items-center">
                  <button
                    onClick={handlePreviousPage}
                    disabled={pagination.offset === 0 || loading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    {pagination.offset + 1}-
                    {Math.min(
                      pagination.offset + pagination.limit,
                      pagination.total,
                    )}{" "}
                    of {pagination.total}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={
                      pagination.offset + pagination.limit >=
                        pagination.total || loading
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Review Details Panel */}
          <div className="lg:col-span-1">
            {selectedReview ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Review Details
                </h3>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Rating</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {selectedReview.rating} ⭐
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">Comment</p>
                    <p className="text-gray-700 mt-1 text-sm">
                      {selectedReview.comment}
                    </p>
                  </div>

                  {selectedReview.image_url && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Image</p>
                      <img
                        src={getReviewImageUrl(selectedReview.image_url)}
                        alt="Review attachment"
                        className="mt-2 w-full max-h-56 rounded-lg object-cover border border-gray-200"
                      />
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-medium text-gray-500">Author</p>
                    <p className="text-gray-900 mt-1">
                      {selectedReview.User?.first_name || ""}{" "}
                      {selectedReview.User?.last_name || "Unknown"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">Product</p>
                    <p className="text-gray-900 mt-1">
                      {selectedReview.Product?.name_en || "Unknown Product"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <p className="text-gray-900 mt-1">
                      {selectedReview.approved ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Approved
                        </span>
                      ) : selectedReview.rejection_reason ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          Rejected: {selectedReview.rejection_reason}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                          Pending approval
                        </span>
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Submitted
                    </p>
                    <p className="text-gray-700 mt-1 text-sm">
                      {new Date(selectedReview.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <hr className="my-4" />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason
                    </label>
                    <select
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Inappropriate content">
                        Inappropriate content
                      </option>
                      <option value="Spam">Spam</option>
                      <option value="Harassment">Harassment</option>
                      <option value="False information">
                        False information
                      </option>
                      <option value="Offensive language">
                        Offensive language
                      </option>
                      <option value="Not a valid review">
                        Not a valid review
                      </option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    {!selectedReview.approved && (
                      <button
                        onClick={() => handleApprove(selectedReview.review_id)}
                        disabled={actionLoading}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        ✓ Approve
                      </button>
                    )}
                    {selectedReview.approved && (
                      <button
                        onClick={() => handleReject(selectedReview.review_id)}
                        disabled={actionLoading}
                        className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        ✗ Reject
                      </button>
                    )}
                    {!selectedReview.approved && (
                      <button
                        onClick={() => handleReject(selectedReview.review_id)}
                        disabled={actionLoading}
                        className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        ✗ Reject
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(selectedReview.review_id)}
                      disabled={actionLoading}
                      className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                <p>Select a review to see details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
